import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Box, Card, CardContent, List, ListItem,
  ListItemText, ListItemIcon, Divider, Modal, Paper
} from '@mui/material';
import { useNavigate, useOutletContext } from 'react-router-dom';
import PropertyFooterLayout from '../../../components/properties/PropertyFooterLayout';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SettingsIcon from '@mui/icons-material/Settings';
import StarIcon from '@mui/icons-material/Star';
import { Property } from '../../../types/Property';
import { getPhotosFromIndexedDB } from '../../../utils/indexDB';

type PublishOutletContext = {
  handleNavigateToFinish: () => void;
  property: Property;
  propertyCurrency: string;
};

const progress = 90;
const step = 3;

const PropertySummaryPage: React.FC = () => {
  const [openModal, setOpenModal] = useState(false);
  const [firstPhoto, setFirstPhoto] = useState<string | null>(null); // Estado para la primera foto
  const navigate = useNavigate();
  const { handleNavigateToFinish, property, propertyCurrency } = useOutletContext<PublishOutletContext>();

  // Cargar la primera foto desde IndexedDB
  useEffect(() => {
    const loadFirstPhoto = async () => {
      const photos = await getPhotosFromIndexedDB();
      if (photos.length > 0) {
        const firstPhotoFile = photos[0]; // Obtener el primer archivo
        const firstPhotoUrl = URL.createObjectURL(firstPhotoFile); // Crear una URL para el archivo
        setFirstPhoto(firstPhotoUrl); // Guardar la URL en el estado
      }
    };
    loadFirstPhoto();
  }, []);

  const handleBack = () => {
    navigate(-1);
  };

  const handleContinue = () => {
    handleNavigateToFinish();
  };

  const handleCardClick = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const formatPrice = (price: number) => {
    if (propertyCurrency === 'CRC') {
      return `₡${price.toLocaleString('es-CR')}`;
    } else {
      return `$${price.toLocaleString('en-US')}`;
    }
  };

  return (
    <Container maxWidth="md" sx={{ display: 'flex', pb: 10, flexDirection: 'column', alignItems: 'center' }}>
      <Box mb={8} sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
        <Box sx={{ width: '100%', maxWidth: '1200px' }}>
          <Typography variant="h4" gutterBottom textAlign="center">
            Revisa tu publicación
          </Typography>
          <Typography variant="body1" mb={4} textAlign="center">
            Esto es lo que mostraremos a los usuarios. Asegúrate de que todo luzca bien.
          </Typography>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 4,
              flexDirection: { xs: 'column', md: 'row' }, // Cambia a columna en pantallas pequeñas
              alignItems: { xs: 'center', md: 'flex-start' }, // Centra el contenido en columnas pequeñas
              width: '100%',
            }}
          >
            {/* Vista previa de la publicación */}
            <Card
              sx={{
                width: { xs: '100%', sm: '330px' }, // Ocupa todo el ancho en pantallas pequeñas
                m: 2,
                borderRadius: 2,
                boxShadow: 3,
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                height: '400px',
                '&:hover': {
                  boxShadow: 6,
                },
              }}
              onClick={handleCardClick}
            >
              <Box sx={{ flex: '0 0 65%', overflow: 'hidden' }}>
                {firstPhoto ? (
                  <img
                    src={firstPhoto}
                    alt="Vista previa"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <Typography variant="body2" textAlign="center" color="textSecondary">
                    Cargando foto...
                  </Typography>
                )}
              </Box>
              <CardContent sx={{ flex: '1 1 auto' }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }} gutterBottom>
                  {property.title}
                </Typography>
                <Typography variant="h6" sx={{ mt: 1 }}>
                  {formatPrice(property.price)}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 1 }}>
                  <Typography variant="body2" color="textSecondary" sx={{ mr: 1 }}>
                    Novedad
                  </Typography>
                  <StarIcon sx={{ color: 'gold' }} />
                </Box>
              </CardContent>
            </Card>

            {/* Próximos pasos */}
            <Box sx={{ width: { xs: '100%', sm: '400px' } }}> {/* Ocupa todo el ancho en pantallas pequeñas */}
              <Typography mt={1} variant="h6" sx={{ fontWeight: 'bold' }} gutterBottom>
                ¿Cuál es el siguiente paso?
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Confirma algunos detalles y publica el anuncio"
                    secondary="Te avisaremos si tienes que verificar tu identidad o registrarte ante las autoridades locales."
                  />
                </ListItem>
                <Divider />
                <ListItem>
                  <ListItemIcon>
                    <CalendarTodayIcon color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Configura el calendario"
                    secondary="Elige las fechas en las que tu propiedad esté disponible. El anuncio aparecerá inmediatamente después de que lo publiques."
                  />
                </ListItem>
                <Divider />
                <ListItem>
                  <ListItemIcon>
                    <SettingsIcon color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Ajusta la configuración"
                    secondary="Establece las reglas de la propiedad, selecciona una política de cancelación, decide cómo se contactará y más."
                  />
                </ListItem>
              </List>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Modal de vista previa */}
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2, 
        }}
      >
        <Paper
          sx={{
            width: { xs: '90%', sm: '600px' }, 
            p: 4,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' }, 
            borderRadius: 2,
            boxShadow: 24,
            alignItems: { xs: 'center', sm: 'flex-start' },
          }}
        >
          {firstPhoto && (
            <img
              src={firstPhoto}
              alt="Vista previa"
              style={{
                width: '250px',
                height: '150px',
                objectFit: 'cover',
                borderRadius: 8,
                marginBottom: '16px', 
              }}
            />
          )}
          <Box
            sx={{
              ml: { sm: 2 },
              textAlign: { xs: 'center', sm: 'left' }, 
            }}
          >
            <Typography variant="h5" gutterBottom>
              {property.title}
            </Typography>
            <Typography variant="body1" paragraph>
              {property.description}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {property.addressDescription}
            </Typography>
            <Typography variant="h6" sx={{ mt: 2 }}>
              Precio: {formatPrice(property.price)}
            </Typography>
          </Box>
        </Paper>
      </Modal>

      <PropertyFooterLayout
        progress={progress}
        step={step}
        onBack={handleBack}
        onContinue={handleContinue}
        isContinueDisabled={false}
      />
    </Container>
  );
};

export default PropertySummaryPage;
